import React, { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Grid,
  Paper,
  TextField,
  Typography,
  createTheme,
  useMediaQuery,
} from "@mui/material";
import BadgeIcon from "@mui/icons-material/Badge";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import EditIcon from "@mui/icons-material/Edit";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import SaveIcon from "@mui/icons-material/Save";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import WorkspacePremiumIcon from "@mui/icons-material/EmojiEvents";
import { useDispatch, useSelector } from "react-redux";
import { createAxios } from "../../createAxios";
import {
  updateUser,
  uploadImage,
} from "../../redux/apiRequest";
import { getUserId } from "../../utils/authSession";
import { useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "../../queries/queryKeys";
import { useProfileQuery } from "../../queries/useToeicQueries";

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "14px",
    bgcolor: "var(--toeic-surface)",
    "& fieldset": { borderColor: "var(--toeic-line)", borderStyle: "dotted" },
    "&:hover fieldset": { borderColor: "var(--toeic-sky)" },
    "&.Mui-focused fieldset": { borderColor: "var(--toeic-sky)", borderWidth: 1.5 },
  },
  "& .MuiInputLabel-root": { fontWeight: 500 },
  "& .MuiInputLabel-root.Mui-focused": { color: "var(--toeic-sky-deep)" },
};

function Profile() {
  const [isEditing, setIsEditing] = useState(false);
  const [avatarFile, setAvatarFile] = useState(null);
  const currentUser = useSelector((state) => state.user.signin.currentUser);
  const accessToken = currentUser?.metadata.tokens.accessToken;
  const userID = getUserId(currentUser);
  const dispatch = useDispatch();
  const axiosJWT = createAxios(currentUser);
  const queryClient = useQueryClient();
  const [profile, setProfile] = useState({
    name: "",
    email: "",
    avatar: "",
    phone: "",
    gender: "male",
    birthDay: "01",
    birthMonth: "01",
    birthYear: "2000",
    createdAt: "2024-01-01",
    totalTests: 0,
    highestScore: 0,
  });

  const theme = createTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { data: profileData } = useProfileQuery(userID);

  useEffect(() => {
    if (profileData && !isEditing) {
      setProfile(profileData);
    }
  }, [isEditing, profileData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfile((prevProfile) => ({ ...prevProfile, [name]: value }));
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfile((prevProfile) => ({ ...prevProfile, avatar: reader.result }));
      };
      reader.readAsDataURL(file);
      setAvatarFile(file);
    }
  };

  const handleSaveClick = async () => {
    try {
      let avatar = profile.avatar;
      if (avatarFile) {
        const uploadedImage = await uploadImage(avatarFile, "", dispatch);
        if (uploadedImage?.img_url) avatar = uploadedImage.img_url;
      }

      const updatedProfile = {
        ...profile,
        birthday: new Date(`${profile.birthYear}-${profile.birthMonth}-${profile.birthDay}`).toISOString(),
        avatar,
      };

      await updateUser(accessToken, userID, updatedProfile, dispatch, axiosJWT);
      await queryClient.invalidateQueries({
        queryKey: queryKeys.history.profile(userID),
      });
      await queryClient.invalidateQueries({
        queryKey: queryKeys.users.detail(userID),
      });
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating profile:", error);
    }
  };

  return (
    <Box sx={{ width: "100%", mt: 4, mb: 6 }}>
      <Box component="header">
        <Box sx={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr auto", gap: 2, alignItems: "center" }}>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <Box sx={{ width: 58, height: 58, borderRadius: "16px", display: "grid", placeItems: "center", bgcolor: "#f0ecff", color: "#6d4aff" }}>
              <BadgeIcon sx={{ fontSize: 38 }} />
            </Box>
            <Box>
              <Chip label="Learner profile" size="small" sx={{ mb: 1.25, bgcolor: "#e8f9ed", color: "#1f9b45", fontWeight: 700 }} />
              <Typography component="h1" sx={{ color: "var(--toeic-ink-deep)", fontSize: { xs: 34, md: 52 }, lineHeight: { xs: 1.05, md: 1 }, fontWeight: 800 }}>
                <Box component="span" sx={{ position: "relative", display: "inline-block", "&::before": { content: '""', position: "absolute", left: "-0.12em", right: "-0.12em", bottom: "0.04em", height: "0.44em", bgcolor: "rgba(109, 74, 255, 0.16)", borderRadius: "0.45em 0.6em 0.4em 0.55em", transform: "rotate(-1deg)" } }}>
                  <Box component="span" sx={{ position: "relative" }}>Your Profile</Box>
                </Box>
              </Typography>
              <Typography sx={{ mt: 1.5, color: "var(--toeic-ink-soft)", fontSize: 17, fontWeight: 700, lineHeight: 1.65 }}>
                Manage your personal information and study identity.
              </Typography>
            </Box>
          </Box>
          <Button
            variant={isEditing ? "contained" : "outlined"}
            startIcon={isEditing ? <SaveIcon /> : <EditIcon />}
            onClick={isEditing ? handleSaveClick : () => setIsEditing(true)}
            sx={{
              justifySelf: isMobile ? "start" : "end",
              borderRadius: "14px",
              bgcolor: isEditing ? "#35c759" : "transparent",
              color: isEditing ? "#ffffff" : "#2196f3",
              borderColor: "#2196f3",
              "&:hover": { bgcolor: isEditing ? "#2fb44f" : "#edf7ff" },
            }}
          >
            {isEditing ? "Save profile" : "Edit profile"}
          </Button>
        </Box>
      </Box>

      <Box sx={{ mt: 4, mb: 4, display: "grid", gap: 4 }}>
        <Box aria-hidden sx={{ borderTop: "1.5px dotted var(--toeic-line)" }} />
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "320px minmax(0, 1fr)", gap: 3, alignItems: "start" }}>
        <Paper elevation={0} sx={{ p: 3, borderRadius: "14px", border: 0, boxShadow: "var(--toeic-shadow-card)", bgcolor: "var(--toeic-surface)", textAlign: "center" }}>
          <Avatar alt={profile.name} src={profile.avatar} sx={{ width: 132, height: 132, mx: "auto", mb: 2, bgcolor: "#edf7ff", color: "#2196f3", fontSize: 42, fontWeight: 700 }}>
            {profile.name?.charAt(0)?.toUpperCase()}
          </Avatar>
          <Typography sx={{ color: "var(--toeic-ink-deep)", fontWeight: 700, fontSize: 22 }}>{profile.name || "Learner"}</Typography>
          <Typography sx={{ color: "var(--toeic-ink-soft)", fontWeight: 700 }}>{profile.email}</Typography>
          {isEditing && (
            <Button variant="outlined" component="label" startIcon={<UploadFileIcon />} sx={{ mt: 2, borderRadius: "14px", borderStyle: "dotted" }}>
              Change avatar
              <input type="file" accept="image/*" hidden onChange={handleAvatarChange} />
            </Button>
          )}
          <Box sx={{ mt: 3, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
            {[
              ["Tests", profile.totalTests, WorkspacePremiumIcon, "#2196f3", "#edf7ff"],
              ["Best", profile.highestScore, EmojiEventsIcon, "#b77900", "#fff7d6"],
            ].map(([label, value, Icon, color, bg]) => (
              <Box key={label} sx={{ p: 1.5, borderRadius: "14px", bgcolor: bg, border: 0 }}>
                <Icon sx={{ color }} />
                <Typography sx={{ color, fontWeight: 700 }}>{value}</Typography>
                <Typography sx={{ color: "var(--toeic-ink-soft)", fontSize: 12, fontWeight: 800 }}>{label}</Typography>
              </Box>
            ))}
          </Box>
          <Chip icon={<CalendarMonthIcon />} label={`Joined ${profile.createdAt}`} sx={{ mt: 2, bgcolor: "#e8f6ff", color: "var(--toeic-sky-deep)", fontWeight: 700 }} />
        </Paper>

        <Paper elevation={0} sx={{ p: isMobile ? 2 : 3, borderRadius: "14px", border: 0, boxShadow: "var(--toeic-shadow-card)", bgcolor: "var(--toeic-surface)" }}>
          <Grid container spacing={2}>
            {[
              ["Name", "name", profile.name, 12],
              ["Email", "email", profile.email, 12],
              ["Phone", "phone", profile.phone, 6],
              ["Gender", "gender", profile.gender, 6],
              ["Day", "birthDay", profile.birthDay, 4],
              ["Month", "birthMonth", profile.birthMonth, 4],
              ["Year", "birthYear", profile.birthYear, 4],
            ].map(([label, name, value, xs]) => (
              <Grid item xs={12} sm={xs} key={name}>
                <TextField
                  fullWidth
                  label={label}
                  variant="outlined"
                  value={value}
                  name={name}
                  onChange={handleInputChange}
                  InputProps={{ readOnly: !isEditing }}
                  sx={fieldSx}
                />
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Box>
    </Box>
  );
}

export default Profile;
